<template>
  <section class="mx-auto flex min-h-[70vh] max-w-5xl flex-col gap-8 px-6 py-16">
    <div class="rounded-3xl border border-white/10 bg-midnight-800/60 p-6 shadow-glow sm:p-8">
      <div class="flex flex-col gap-6 sm:flex-row sm:items-center">
        <div class="flex items-center gap-4">
          <div class="h-24 w-24 overflow-hidden rounded-3xl border border-white/10 bg-midnight-900/70">
            <img
              v-if="displayAvatarUrl"
              :src="displayAvatarUrl"
              alt="Profile avatar"
              class="h-full w-full object-cover"
              referrerpolicy="no-referrer"
              crossorigin="anonymous"
            />
            <div v-else class="flex h-full w-full items-center justify-center text-3xl font-semibold text-steel-200">
              {{ initials }}
            </div>
          </div>
          <div>
            <p class="text-xs uppercase tracking-[0.35em] text-steel-400">Public Profile</p>
            <h1 class="mt-2 font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">
              {{ profile.full_name || 'DRVN Member' }}
            </h1>
            <p class="text-sm text-steel-400">@{{ handle }}</p>
          </div>
        </div>
        <div class="flex flex-wrap items-center gap-3 sm:ml-auto">
          <div class="rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-2 text-xs uppercase tracking-[0.3em] text-steel-400">
            Likes
            <span class="ml-2 text-sm font-semibold text-white">{{ totalLikesLabel }}</span>
          </div>
          <a
            v-if="instagramUrl"
            :href="instagramUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-white/10 bg-midnight-900/60 text-steel-300 transition hover:border-white/30 hover:text-white"
            aria-label="Instagram"
          >
            <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
              <rect x="3" y="3" width="18" height="18" rx="5" ry="5" stroke-width="1.6" />
              <circle cx="12" cy="12" r="4" stroke-width="1.6" />
              <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
            </svg>
          </a>
        </div>
      </div>

      <div
        v-if="profile.status === 'banned'"
        class="mt-6 rounded-2xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-100"
      >
        This account has been banned. Content and interactions may be restricted.
      </div>

      <div class="mt-8 grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
        <div class="flex h-full flex-col gap-5">
          <p class="text-sm uppercase tracking-[0.3em] text-steel-400">Bio</p>
          <p class="text-base text-steel-200/90">
            {{ profile.bio || 'This member has not added a bio yet.' }}
          </p>
          <div class="grid gap-3 sm:grid-cols-3">
            <div class="rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3">
              <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">Posts</p>
              <p class="mt-2 text-lg font-semibold text-white">{{ postCount }}</p>
            </div>
            <div class="rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3">
              <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">Total Likes</p>
              <p class="mt-2 text-lg font-semibold text-white">{{ totalLikesLabel }}</p>
            </div>
            <div class="rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3">
              <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">Latest Post</p>
              <p class="mt-2 text-sm font-semibold text-white">{{ latestPostLabel }}</p>
            </div>
          </div>
          <div class="grid gap-3 sm:grid-cols-2">
            <div class="rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3">
              <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">Pinned</p>
              <p class="mt-2 text-sm text-white">{{ profile.pinned_status || 'No pinned status yet.' }}</p>
            </div>
            <div class="rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3">
              <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">Badges</p>
              <div class="mt-2 flex flex-wrap gap-2">
                <span
                  v-for="badge in achievementBadges"
                  :key="badge.label"
                  class="inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em]"
                  :class="badge.tone"
                >
                  {{ badge.label }}
                </span>
                <span v-if="!achievementBadges.length" class="text-xs text-steel-400">No badges yet.</span>
              </div>
            </div>
          </div>
          <div v-if="posts.length" class="mt-auto rounded-2xl border border-white/10 bg-midnight-900/60 p-4">
            <p class="text-xs uppercase tracking-[0.3em] text-steel-400">Spotlight</p>
            <router-link :to="`/post/${posts[0].id}`" class="mt-3 flex items-center gap-4">
              <div class="h-16 w-16 overflow-hidden rounded-2xl border border-white/10 bg-midnight-900/60">
                <img
                  :src="postMediaUrl(posts[0])"
                  :style="{ objectPosition: postMediaPosition(posts[0]) }"
                  alt="Post preview"
                  class="h-full w-full object-cover"
                />
              </div>
              <div class="min-w-0">
                <p class="truncate text-sm text-white">{{ posts[0].caption || 'Untitled build' }}</p>
                <p class="mt-1 text-xs text-steel-400">{{ posts[0].description || 'Tap to view build details.' }}</p>
              </div>
            </router-link>
          </div>
        </div>
        <div class="space-y-5 rounded-2xl border border-white/10 bg-midnight-900/40 p-5">
          <div>
            <p class="text-xs uppercase tracking-[0.3em] text-steel-400">Member Since</p>
            <p class="mt-2 text-sm text-steel-200">{{ memberSince }}</p>
          </div>
          <div v-if="profile.tags && profile.tags.length" class="border-t border-white/5 pt-5">
            <p class="text-xs uppercase tracking-[0.3em] text-steel-400">Interests</p>
            <div class="mt-3 flex flex-wrap gap-2">
              <span
                v-for="tag in profile.tags"
                :key="tag"
                class="inline-block rounded-full bg-neon-400/10 px-3 py-1 text-xs font-medium text-neon-400 border border-neon-400/20"
              >
                {{ tag }}
              </span>
            </div>
          </div>
          <div>
            <p class="text-xs uppercase tracking-[0.3em] text-steel-400">Role</p>
            <div class="mt-2">
              <span class="inline-block rounded-full px-3 py-1 text-xs font-medium uppercase tracking-[0.2em] border" :class="roleBadgeClass">
                {{ roleLabel }}
              </span>
            </div>
          </div>
          <div v-if="profile.country">
            <p class="text-xs uppercase tracking-[0.3em] text-steel-400">Country</p>
            <p class="mt-2 text-sm text-steel-200">{{ profile.country }}</p>
          </div>
          <div v-if="profile.city">
            <p class="text-xs uppercase tracking-[0.3em] text-steel-400">City</p>
            <p class="mt-2 text-sm text-steel-200">{{ profile.city }}</p>
          </div>
          <div>
            <p class="text-xs uppercase tracking-[0.3em] text-steel-400">Status</p>
            <p class="mt-2 text-sm text-steel-200">{{ statusLabel }}</p>
          </div>
        </div>
      </div>

      <div class="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <router-link
          v-for="post in posts"
          :key="post.id"
          :to="`/post/${post.id}`"
          class="overflow-hidden rounded-2xl border border-white/10 bg-midnight-900/60 transition hover:border-white/20"
        >
          <div class="relative w-full aspect-square">
            <transition :name="post.mediaDirection === 'prev' ? 'media-swipe-prev' : 'media-swipe-next'" mode="out-in">
              <img
                :key="postMediaUrl(post)"
                :src="postMediaUrl(post)"
                alt="Post"
                class="h-full w-full object-cover"
                :style="{ objectPosition: postMediaPosition(post) }"
              />
            </transition>
            <button
              v-if="post.mediaUrls?.length > 1"
              type="button"
              class="absolute left-2 top-1/2 -translate-y-1/2 rounded-full border border-white/20 bg-black/40 px-2 py-1 text-xs text-white"
              @click.prevent="prevMedia(post)"
            >
              Prev
            </button>
            <button
              v-if="post.mediaUrls?.length > 1"
              type="button"
              class="absolute right-2 top-1/2 -translate-y-1/2 rounded-full border border-white/20 bg-black/40 px-2 py-1 text-xs text-white"
              @click.prevent="nextMedia(post)"
            >
              Next
            </button>
          </div>
          <div class="p-4">
            <p class="text-sm text-steel-200/90">{{ post.caption || 'No caption' }}</p>
            <p v-if="post.description" class="mt-2 text-xs text-steel-400">{{ post.description }}</p>
            <p v-if="post.modifications" class="mt-2 text-xs text-steel-400">Mods: {{ post.modifications }}</p>
            <p class="mt-2 text-xs text-steel-400">Likes: {{ post.likeCount }}</p>
            <div v-if="post.engine || post.hp_stock || post.hp_tuned || post.year" class="mt-2 text-xs text-steel-400">
              <p v-if="post.engine">Engine: {{ post.engine }}</p>
              <p v-if="post.hp_stock">HP Stock: {{ post.hp_stock }}</p>
              <p v-if="post.hp_tuned">HP Tuned: {{ post.hp_tuned }}</p>
              <p v-if="post.year">Year: {{ post.year }}</p>
            </div>
          </div>
        </router-link>
      </div>

      <p v-if="errorMessage" class="mt-6 rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
        {{ errorMessage }}
      </p>
    </div>
  </section>
</template>

<script>
import { supabase, getAvatarUrl } from '@/lib/supabase'

export default {
  name: 'PublicProfileView',
  data() {
    return {
      profile: {
        full_name: '',
        bio: '',
        avatar_url: '',
        avatar_path: '',
        role: '',
        status: '',
        created_at: '',
        instagram_handle: '',
        tags: [],
        pinned_status: '',
      },
      posts: [],
      errorMessage: '',
      totalLikes: 0,
      monthlyTopMembers: [],
      monthlyBadgeLabel: '',
    }
  },
  computed: {
    handle() {
      return this.$route.params.handle
    },
    displayAvatarUrl() {
      return getAvatarUrl({ avatarPath: this.profile.avatar_path, avatarUrl: this.profile.avatar_url })
    },
    initials() {
      if (!this.profile.full_name) return 'D'
      return this.profile.full_name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((chunk) => chunk[0].toUpperCase())
        .join('')
    },
    memberSince() {
      if (!this.profile.created_at) return '—'
      return new Date(this.profile.created_at).toLocaleDateString()
    },
    instagramUrl() {
      if (!this.profile.instagram_handle) return ''
      const cleaned = this.profile.instagram_handle.replace(/^@/, '').trim()
      if (!cleaned) return ''
      return `https://www.instagram.com/${cleaned}`
    },
    postCount() {
      return this.posts.length
    },
    totalLikesLabel() {
      return Number(this.totalLikes || 0).toLocaleString()
    },
    statusLabel() {
      return this.profile.status === 'banned' ? 'Banned' : 'Active'
    },
    roleLabel() {
      return (this.profile.role || 'member')
    },
    roleBadgeClass() {
      const role = (this.profile.role || 'member').toLowerCase()
      if (role === 'admin') return 'bg-rose-500/10 border-rose-500/30 text-rose-200'
      if (role === 'moderator') return 'bg-violet-400/10 border-violet-400/30 text-violet-200'
      return 'bg-neon-400/10 border-neon-400/20 text-neon-400'
    },
    latestPostLabel() {
      if (!this.posts.length || !this.posts[0].created_at) return '—'
      return new Date(this.posts[0].created_at).toLocaleDateString()
    },
    achievementBadges() {
      const badges = []
      if (this.totalLikes >= 10) {
        badges.push({ label: '10 Likes', tone: 'border-white/10 bg-white/5 text-white' })
      }
      if (this.totalLikes >= 50) {
        badges.push({ label: '50 Likes', tone: 'border-neon-400/30 bg-neon-400/10 text-neon-300' })
      }
      if (this.totalLikes >= 100) {
        badges.push({ label: '100 Likes', tone: 'border-amber-400/40 bg-amber-400/10 text-amber-200' })
      }
      if (this.totalLikes >= 500) {
        badges.push({ label: '500 Likes', tone: 'border-amber-400/50 bg-amber-400/20 text-amber-100' })
      }
      if (this.totalLikes >= 1000) {
        badges.push({ label: '1K Likes', tone: 'border-yellow-300/60 bg-yellow-300/20 text-yellow-100' })
      }
      if (this.profile.champions_league_winner) {
        badges.push({
          label: 'Champions League Winner',
          tone: 'border-amber-300/60 bg-amber-300/20 text-amber-100',
        })
      }
      if (this.monthlyTopMembers.length && this.profile?.id && this.monthlyTopMembers.includes(this.profile.id)) {
        badges.push({
          label: this.monthlyBadgeLabel || 'Top 3',
          tone: 'border-amber-400/40 bg-amber-400/10 text-amber-200',
        })
      }
      return badges
    },
  },
  async mounted() {
    await this.fetchProfile()
  },
  watch: {
    '$route.params.handle': async function() {
      // Reset data before fetching new profile
      this.profile = {
        full_name: '',
        bio: '',
        avatar_url: '',
        avatar_path: '',
        role: '',
        champions_league_winner: false,
        status: '',
        created_at: '',
        instagram_handle: '',
        tags: [],
        pinned_status: '',
      }
      this.posts = []
      this.totalLikes = 0
      this.errorMessage = ''
      await this.fetchProfile()
    },
  },
  methods: {
    postMediaUrl(post) {
      return post.mediaUrls?.[post.mediaIndex] || post.imageUrl || ''
    },
    postMediaPosition(post) {
      const offset = post.mediaOffsets?.[post.mediaIndex] || post.mediaOffsets?.[0] || { x: 50, y: 50 }
      return `${offset.x}% ${offset.y}%`
    },
    nextMedia(post) {
      post.mediaDirection = 'next'
      post.mediaIndex = (post.mediaIndex + 1) % post.mediaUrls.length
    },
    prevMedia(post) {
      post.mediaDirection = 'prev'
      post.mediaIndex = (post.mediaIndex - 1 + post.mediaUrls.length) % post.mediaUrls.length
    },
    async fetchProfile() {
      this.errorMessage = ''

    const { data, error } = await supabase
      .from('profiles')
      .select('id, full_name, bio, avatar_url, avatar_path, role, champions_league_winner, status, country, city, created_at, instagram_handle, tags, pinned_status')
      .eq('handle', this.handle)
      .maybeSingle()

      if (error) {
        this.errorMessage = 'Profile data is not available yet.'
        return
      }

      if (!data) {
        this.errorMessage = 'This profile does not exist yet.'
        return
      }

      this.profile = data

      if (!this.profile.avatar_path && this.profile.avatar_url?.includes('/object/sign/')) {
        const pathMatch = this.profile.avatar_url.match(/\/object\/sign\/avatars\/(.+?)\?/)
        if (pathMatch?.[1]) {
          this.profile.avatar_path = pathMatch[1]
        }
      }

      await this.fetchMonthlyTopMembers()
      await this.fetchPosts()
    },
    async fetchMonthlyTopMembers() {
      const now = new Date()
      if (now.getDate() !== 1) {
        this.monthlyTopMembers = []
        this.monthlyBadgeLabel = ''
        return
      }

      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      const end = new Date(now.getFullYear(), now.getMonth(), 1)
      this.monthlyBadgeLabel = `Top 3 — ${start.toLocaleString(undefined, { month: 'short' })}`

      const { data: posts, error } = await supabase
        .from('posts')
        .select('user_id, post_likes (id), created_at')
        .gte('created_at', start.toISOString())
        .lt('created_at', end.toISOString())

      if (error || !posts?.length) {
        this.monthlyTopMembers = []
        return
      }

      const likeMap = new Map()
      ;(posts || []).forEach((post) => {
        const count = (post.post_likes || []).length || 0
        likeMap.set(post.user_id, (likeMap.get(post.user_id) || 0) + count)
      })

      this.monthlyTopMembers = Array.from(likeMap.entries())
        .map(([userId, count]) => ({ userId, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 3)
        .map((entry) => entry.userId)
    },
    async fetchPosts() {
      if (!this.profile?.id) return
      const { data, error } = await supabase
        .from('posts')
        .select('id, caption, description, modifications, engine, hp_stock, hp_tuned, year, image_url, image_path, image_offset_x, image_offset_y, created_at, post_media (image_url, image_path, sort_order, offset_x, offset_y)')
        .eq('user_id', this.profile.id)
        .order('created_at', { ascending: false })

      if (error) return

      this.posts = (data || []).map((post) => {
        const mediaItems = [
          {
            url: post.image_path
              ? supabase.storage.from('posts').getPublicUrl(post.image_path).data?.publicUrl
              : post.image_url,
            offset: {
              x: Number.isFinite(post.image_offset_x) ? post.image_offset_x : 50,
              y: Number.isFinite(post.image_offset_y) ? post.image_offset_y : 50,
            },
          },
          ...(post.post_media || []).map((item) => ({
            url: item.image_path
              ? supabase.storage.from('posts').getPublicUrl(item.image_path).data?.publicUrl
              : item.image_url,
            offset: {
              x: Number.isFinite(item.offset_x) ? item.offset_x : 50,
              y: Number.isFinite(item.offset_y) ? item.offset_y : 50,
            },
          })),
        ].filter((item) => Boolean(item.url))

        return {
          ...post,
          imageUrl: post.image_path
            ? supabase.storage.from('posts').getPublicUrl(post.image_path).data?.publicUrl
            : post.image_url,
          mediaUrls: mediaItems.map((item) => item.url),
          mediaOffsets: mediaItems.map((item) => item.offset),
          mediaIndex: 0,
          mediaDirection: 'next',
          likeCount: 0,
        }
      })
      await this.fetchPostLikeCounts()
      await this.fetchTotalLikes()
    },
    async fetchPostLikeCounts() {
      const postIds = this.posts.map((post) => post.id)
      if (!postIds.length) return

      const { data: likes } = await supabase
        .from('post_likes')
        .select('post_id')
        .in('post_id', postIds)

      const likeCounts = new Map()
      ;(likes || []).forEach((like) => {
        likeCounts.set(like.post_id, (likeCounts.get(like.post_id) || 0) + 1)
      })

      this.posts = this.posts.map((post) => ({
        ...post,
        likeCount: likeCounts.get(post.id) || 0,
      }))
    },
    async fetchTotalLikes() {
      const postIds = this.posts.map((post) => post.id)
      if (!postIds.length) {
        this.totalLikes = 0
        return
      }

      const { count } = await supabase
        .from('post_likes')
        .select('id', { count: 'exact', head: true })
        .in('post_id', postIds)

      this.totalLikes = count || 0
    },
  },
}
</script>

<style>
.media-swipe-next-enter-active,
.media-swipe-next-leave-active,
.media-swipe-prev-enter-active,
.media-swipe-prev-leave-active {
  transition: opacity 220ms ease, transform 220ms ease;
}

.media-swipe-next-enter-from {
  opacity: 0;
  transform: translateX(10%);
}

.media-swipe-next-leave-to {
  opacity: 0;
  transform: translateX(-10%);
}

.media-swipe-prev-enter-from {
  opacity: 0;
  transform: translateX(-10%);
}

.media-swipe-prev-leave-to {
  opacity: 0;
  transform: translateX(10%);
}
</style>

